import * as Sentry from "@sentry/browser";
import axios, { AxiosResponse } from "axios";

import { PropallyV1ApiFactory } from "@/api/generated/api";
import { getAppVersionTag } from "@/utilities/appInfo";
import { extractHttpStatus } from "@/utilities/extractHttpStatus";
import { isRequestAbortedError } from "@/utilities/isRequestAbortedError";
import { isResponselessNetworkError } from "@/utilities/networkError";

const axiosInstance = axios.create();

// CapacitorHttp は JSON の解析に失敗しても文字列を成功応答として返すことがある。
// API の正常な JSON 応答はオブジェクト・配列・数値等で、文字列を返すエンドポイントはない。
// 応答本体はエラーに含めず、既存の catch と Sentry の共通処理へ渡す。
// 更新系も対象。保存済みかは確定できないため、ここから自動再送はしない。
axiosInstance.interceptors.response.use((response: AxiosResponse) => {
  const contentType = String(response.headers?.["content-type"] ?? response.headers?.["Content-Type"] ?? "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  const isJson = contentType === "application/json" || /^application\/[\w.+-]+\+json$/.test(contentType);
  if (response.status !== 204 && isJson && typeof response.data === "string" && response.data !== "") {
    throw Object.assign(new Error("Invalid JSON API response"), {
      name: "InvalidApiResponseError",
      code: "ERR_BAD_RESPONSE",
      isAxiosError: true,
      config: response.config,
      response: { status: response.status, data: {} },
    });
  }
  return response;
});

// MEMO:
// Sentry 側でノイズになる既知の検証エラーコードをここに追加していく。
// 例）会員登録時の想定 422 エラーなど、フロントでハンドリング済みのケース。
const IGNORED_422_ERROR_CODES = new Set<string>([
  "REGISTERED",
  "INVALID_LOAN_HISTORY",
  "INVALID_ANNUAL_INCOME",
  "INVALID_PHONE_NUMBER_AUTH_CODE",
  "REGISTERED_WITH_EXTERNAL_AUTH",
  "EMAIL_ALREADY_TAKEN",
  "EXTERNAL_AUTH_CANNOT_CHANGE_EMAIL",
]);

// MEMO:
// GET の 404 が「未作成」を表す正常系であり、フロントでハンドリング済みのエンドポイント。
// 例）投資スタイル診断は未回答が 404 で、ホーム画面から毎回問い合わせるため
// 除外しないと未診断ユーザー全員分の正常系イベントが Sentry を埋める。
const IGNORED_404_GET_PATHS = new Set<string>(["/v1/investment_style_diagnosis"]);
// 個別オファーの非表示・削除、退会・提携終了・プランナー一覧表示OFF（一時停止）したプランナーの詳細は想定内。
// 存在しない ID と非表示を API が区別しないため、正の整数 ID の詳細 GET 404 は案内画面で扱う。
// 子パス・一覧 API・不正な ID・500 系は除外せず、想定内の 404 で Sentry を埋めない。
const EXPECTED_404_DETAIL_PATH = /^\/v1\/(investment_proposals|assessment_proposals|agents)\/[1-9]\d*$/;

// MEMO:
// POST の 401 が「認証情報が正しくない」を表す正常系であり、フロントでハンドリング済みのエンドポイント。
// 例）ログイン失敗時のパスワード誤りや未登録アカウントは画面上でメッセージ表示まで完結しており、
// 除外しないと日常的なログインミスが Sentry のエラーイベントとして積み上がる。
// 画面アクセスログ（/v1/user_access_screen_logs）は失敗しても UI に影響しない fire-and-forget の計測で、
// セッション切れ時の 401 は想定内のため除外する。
const IGNORED_401_POST_PATHS = new Set<string>([
  "/v1/login",
  "/v1/login_google",
  "/v1/login_apple",
  "/v1/user_access_screen_logs",
]);

// MEMO:
// POST の 429 が「再送間隔内の再リクエスト」を表す正常系であり、フロントでハンドリング済みのエンドポイント。
// 例）SMS 認証コードの送信は同じ番号へ 60 秒に 1 回に制限しており、画面側で残り秒数のカウントダウンを出して完結している。
// ログインの失敗回数による一時ロックも、画面で「時間をおいて再度お試しください」と出して完結している。
const IGNORED_429_POST_PATHS = new Set<string>(["/v1/send_phone_number_auth_codes", "/v1/login"]);

// MEMO:
// アプリ最新バージョン確認（GET /v1/min_app_version）はベストエフォートのバックグラウンド処理で、
// 失敗しても「更新モーダルが出ない」だけでユーザー操作は妨げない。
// 端末のオフライン・電波断でレスポンスを伴わずに失敗するケースはノイズなので除外する。
// 5xx などサーバから応答があるエラーは従来どおり送る（サーバ側の異常検知に必要なため）。
const BEST_EFFORT_NETWORK_ERROR_GET_PATHS = new Set<string>(["/v1/min_app_version"]);

// MEMO:
// ウェビナー視聴ログの更新（PATCH /v1/webinars/:id/view_histories/:id）はベストエフォートのバックグラウンド送信で、
// progress は 5 秒ごとに累積値で送り直されるため、レスポンスを伴わない通信エラーはノイズとして除外する。
// サーバから応答のある 4xx / 5xx は従来どおり送る。
// 完了（completed: true）の取りこぼしは呼び出し側（viewingTracker）が error ログで検知する。
// パスパラメータを含むため完全一致の Set ではなく正規表現で照合する（ID は 0 始まりを許さない正整数）。
const BEST_EFFORT_NETWORK_ERROR_PATCH_PATH_PATTERNS: RegExp[] = [
  /^\/v1\/webinars\/[1-9]\d*\/view_histories\/[1-9]\d*$/,
];

// config.url は basePath を含む絶対URL。末尾一致だと将来の類似パス
// （例: /v1/users/{id}/investment_style_diagnosis）まで巻き込むため、パス全体の完全一致で照合する。
// 相対URL等で URL パースに失敗した場合はクエリを落とした文字列をそのままパスとして扱う。
const extractPathname = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split("?")[0];
  }
};

// MEMO:
// Sentry の fingerprint 用にパスを正規化する。数値だけのセグメントを :id に置き換え、
// /v1/agents/123 と /v1/agents/456 のような ID 違いが別イシューに分裂しないようにする。
// /v1/login_google のように数字を含むだけでセグメント全体が数字ではないものは対象外。
// 住所・駅マスタは文字列をパスパラメータに持つため、api_schema/api.yml の該当ルートだけを
// 正規化する。地名・路線名ごとの分裂を防ぎ、一覧や別ルートはそのまま区別する。
const normalizePathForFingerprint = (pathname: string): string =>
  pathname
    .replace(
      /^\/v1\/master_data\/prefectures\/[^/]+\/cities$/,
      "/v1/master_data/prefectures/:prefecture_name/cities"
    )
    .replace(
      /^\/v1\/master_data\/prefectures\/[^/]+\/cities\/[^/]+\/towns$/,
      "/v1/master_data/prefectures/:prefecture_name/cities/:city_name/towns"
    )
    .replace(/^\/v1\/master_data\/railways\/[^/]+\/stations$/, "/v1/master_data/railways/:railway_name/stations")
    .replace(/\/\d+(?=\/|$)/g, "/:id");

// MEMO:
// AxiosError の message は "Request failed with status code 422" のようにステータスだけで決まり、
// minify 済みの本番ビルドではスタックも同一になるため、既定のグルーピングだと
// エンドポイントが違っても同一ステータスの API エラーが 1 イシューに集約されてしまう。
// そこで「メソッド + 正規化パス + ステータス」で fingerprint を固定し、イシューをエンドポイント単位に分割する。
// config.url が取れない場合は undefined を返し、Sentry のデフォルトグルーピングに任せる。
// レスポンスを伴わないネットワークエラーは status を "network_error" として 1 つにまとめる。
export const buildSentryFingerprint = (error: unknown): string[] | undefined => {
  const config = (error as { config?: { method?: string; url?: string } }).config;
  if (!config?.url) return undefined;
  const method = String(config.method ?? "").toUpperCase() || "UNKNOWN";
  const path = normalizePathForFingerprint(extractPathname(config.url));
  const status = extractHttpStatus(error);
  return [method, path, status ? String(status) : "network_error"];
};

export const shouldSkipSentryReport = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  // MEMO:
  // ユーザーによるページ離脱・遷移や AbortController によるキャンセルで発生する中断は
  // response を持たないため、response の有無を見るより前に判定する必要がある。
  // アプリの不具合ではなくユーザー操作起因のノイズなので Sentry には送らない。
  if (isRequestAbortedError(error)) return true;
  const response = (error as { response?: { status?: number; data?: { code?: string } } }).response;
  if (!response) {
    const config = (error as { config?: { method?: string; url?: string } }).config;
    if (!config?.url) return false;
    if (!isResponselessNetworkError(error)) return false;
    const method = String(config.method ?? "").toLowerCase();
    const path = extractPathname(config.url);
    if (method === "get") return BEST_EFFORT_NETWORK_ERROR_GET_PATHS.has(path);
    if (method === "patch") return BEST_EFFORT_NETWORK_ERROR_PATCH_PATH_PATTERNS.some((pattern) => pattern.test(path));
    return false;
  }
  if (response.status === 422) {
    const code = response.data?.code;
    if (!code) return false;
    return IGNORED_422_ERROR_CODES.has(code);
  }
  if (response.status === 404) {
    const config = (error as { config?: { method?: string; url?: string } }).config;
    if (!config?.url) return false;
    if (String(config.method ?? "").toLowerCase() !== "get") return false;
    const path = extractPathname(config.url);
    return IGNORED_404_GET_PATHS.has(path) || EXPECTED_404_DETAIL_PATH.test(path);
  }
  if (response.status === 401) {
    const config = (error as { config?: { method?: string; url?: string } }).config;
    if (!config?.url) return false;
    if (String(config.method ?? "").toLowerCase() !== "post") return false;
    return IGNORED_401_POST_PATHS.has(extractPathname(config.url));
  }
  if (response.status === 429) {
    const config = (error as { config?: { method?: string; url?: string } }).config;
    if (!config?.url) return false;
    if (String(config.method ?? "").toLowerCase() !== "post") return false;
    return IGNORED_429_POST_PATHS.has(extractPathname(config.url));
  }
  return false;
};

// Sentry に載せる API エラーの情報。
// リクエスト本文・ヘッダ・応答本体はパスワード・SMS コード・SNS ログインのトークン・年収・書類の base64 等を
// 含むため送らない。調査に必要な method・パス・status・エラーコード・メッセージ・検証エラー（errors）だけを送る。
// URL のクエリも検索条件などの入力値が載るため外す（誰に起きたかは Sentry の user.id で辿る）
export const buildSentryApiErrorContext = (
  error: unknown
): { tags: Record<string, string>; extras: Record<string, unknown> } => {
  const tags: Record<string, string> = {};
  const extras: Record<string, unknown> = {};
  const request = (error as { config?: { method?: string; url?: string; baseURL?: string } } | null)?.config;
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;

  if (request?.method) {
    tags["http.method"] = String(request.method).toUpperCase();
  }
  if (request?.url) {
    extras["request.url"] = String(request.url).split("?")[0];
  }
  if (request?.baseURL) {
    extras["request.baseURL"] = request.baseURL;
  }

  if (response?.status) {
    tags["http.status"] = String(response.status);
  }
  const data = response?.data;
  if (typeof data === "object" && data !== null) {
    const { code, message, errors } = data as { code?: unknown; message?: unknown; errors?: unknown };
    if (typeof code === "string" && code) {
      tags["api.error_code"] = code;
    }
    if (message) {
      extras["api.error_message"] = message;
    }
    if (errors) {
      extras["api.errors"] = errors;
    }
  }

  return { tags, extras };
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (shouldSkipSentryReport(error)) {
      // イベントとしては送らないが、サーバ側退行で既知の正常系エラーが急増した場合など、
      // 後続の別エラーを調査する際に「直前に何が起きていたか」を追えるよう breadcrumb だけ残す。
      // breadcrumb は何らかのイベントが送信されるまでローカルに留まるため、Sentry を埋めることはない
      const status = extractHttpStatus(error);
      const config = (error as { config?: { method?: string; url?: string } }).config;
      Sentry.addBreadcrumb({
        category: "http",
        level: Sentry.Severity.Info,
        message: `skipped sentry report: ${String(config?.method ?? "").toUpperCase()} ${config?.url?.split("?")[0] ?? "unknown"} -> ${
          status ?? "unknown"
        }`,
      });
    } else {
      Sentry.withScope((scope) => {
        const { tags, extras } = buildSentryApiErrorContext(error);
        Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
        Object.entries(extras).forEach(([key, value]) => scope.setExtra(key, value));

        const appVersion = getAppVersionTag();
        if (appVersion) {
          scope.setTag("app.version", appVersion);
        }

        scope.setExtra("axiosError", {
          message: (error as Error)?.message,
          name: (error as Error)?.name,
          isAxiosError: (error as { isAxiosError?: boolean }).isAxiosError ?? false,
        });

        const fingerprint = buildSentryFingerprint(error);
        if (fingerprint) {
          scope.setFingerprint(fingerprint);
          scope.setTag("http.path", fingerprint[1]);
        }

        Sentry.captureException(error);
      });
    }
    return Promise.reject(error);
  }
);

export const PropallyApiFactory = () => {
  return PropallyV1ApiFactory(undefined, undefined, axiosInstance);
};
